import React, { useState } from 'react'
import styled from 'styled-components'
import { useModal } from '../../contexts/ModalContext/ModalContext'
import { FloorLevel, MapSettings } from '../../types'

type Tool =
  | 'select'
  | 'wall'
  | 'door'
  | 'window'
  | 'room'
  | 'polygon'
  | 'stairs'
  | 'text'
  | 'poi'
  | 'measure'
  | 'pan'
  | 'line'
  | 'rectangle'
  | 'circle'
  | 'fixture-rect'
  | 'fixture-line'
  | 'fixture-point'
  | 'fixture-circle'
  | 'fixture-polygon'

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #d0d7de #f6f8fa;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f6f8fa;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d7de;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #afb8c1;
  }
`

const PanelHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e1e5e9;
  background: #f6f8fa;
`

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #24292f;
`

const ToolSection = styled.div`
  border-bottom: 1px solid #e1e5e9;
`

const SectionHeader = styled.div<{ $isCollapsed: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f6f8fa;
  border-bottom: ${props => (props.$isCollapsed ? 'none' : '1px solid #e1e5e9')};

  &:hover {
    background: #f1f3f6;
  }
`

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #656d76;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CollapseIcon = styled.span<{ $isCollapsed: boolean }>`
  font-size: 10px;
  transition: transform 0.2s ease;
  transform: ${props =>
    props.$isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};
`

const SectionContent = styled.div<{ $isCollapsed: boolean }>`
  padding: ${props => (props.$isCollapsed ? '0 16px' : '16px')};
  max-height: ${props => (props.$isCollapsed ? '0' : '1000px')};
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: ${props => (props.$isCollapsed ? '0' : '1')};
`

const ToolGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`

const ToolButton = styled.button<{ $isActive: boolean }>`
  padding: 12px;
  border: 1px solid ${props => (props.$isActive ? '#0969da' : '#d0d7de')};
  border-radius: 6px;
  background: ${props => (props.$isActive ? '#dbeafe' : 'white')};
  color: ${props => (props.$isActive ? '#0969da' : '#24292f')};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    background: ${props => (props.$isActive ? '#dbeafe' : '#f6f8fa')};
    border-color: ${props => (props.$isActive ? '#0969da' : '#bdc6cf')};
  }

  &:active {
    transform: translateY(1px);
  }
`

const ToolIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`

const ToolName = styled.span`
  font-size: 11px;
`

const LayersSection = styled.div`
  border-bottom: 1px solid #e1e5e9;
`

const FloorButton = styled.button<{ $isActive: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${props => (props.$isActive ? '#0969da' : '#d0d7de')};
  border-radius: 4px;
  background: ${props => (props.$isActive ? '#dbeafe' : 'white')};
  color: ${props => (props.$isActive ? '#0969da' : '#24292f')};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  width: 100%;
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background: ${props => (props.$isActive ? '#dbeafe' : '#f6f8fa')};
    border-color: ${props => (props.$isActive ? '#0969da' : '#bdc6cf')};
  }
`

const FloorInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const FloorName = styled.span`
  font-weight: 500;
`

const FloorElevation = styled.span`
  font-size: 10px;
  color: #656d76;
`

const FloorActions = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
`

const SmallButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  padding: 4px 8px;
  border: 1px solid
    ${props =>
      props.$variant === 'danger'
        ? '#da3633'
        : props.$variant === 'primary'
        ? '#0969da'
        : '#d0d7de'};
  border-radius: 4px;
  background: ${props =>
    props.$variant === 'danger'
      ? '#da3633'
      : props.$variant === 'primary'
      ? '#0969da'
      : 'white'};
  color: ${props =>
    props.$variant === 'danger' || props.$variant === 'primary'
      ? 'white'
      : '#24292f'};
  font-size: 10px;
  cursor: pointer;
  flex: 1;

  &:hover {
    background: ${props =>
      props.$variant === 'danger'
        ? '#b92f2c'
        : props.$variant === 'primary'
        ? '#0860ca'
        : '#f6f8fa'};
  }
`

const Modal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => (props.$isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`

const ModalHeader = styled.div`
  margin-bottom: 16px;
`

const ModalTitle = styled.h3`
  margin: 0;
  color: #24292f;
  font-size: 16px;
`

const ModalBody = styled.div`
  margin-bottom: 20px;
`

const FormGroup = styled.div`
  margin-bottom: 12px;
`

const Label = styled.label`
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #24292f;
`

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  font-size: 12px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0969da;
    box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
  }

  &[type='file'] {
    padding: 4px;
    font-size: 10px;
  }
`

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

const Button = styled.button<{ $variant?: 'primary' | 'danger' }>`
  padding: 8px 16px;
  border: 1px solid
    ${props =>
      props.$variant === 'danger'
        ? '#da3633'
        : props.$variant === 'primary'
        ? '#0969da'
        : '#d0d7de'};
  border-radius: 4px;
  background: ${props =>
    props.$variant === 'danger'
      ? '#da3633'
      : props.$variant === 'primary'
      ? '#0969da'
      : 'white'};
  color: ${props =>
    props.$variant === 'danger' || props.$variant === 'primary'
      ? 'white'
      : '#24292f'};
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: ${props =>
      props.$variant === 'danger'
        ? '#b92f2c'
        : props.$variant === 'primary'
        ? '#0860ca'
        : '#f6f8fa'};
  }
`

const FloorControlButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const EyeButton = styled.button<{ $isVisible: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 2px;
  opacity: ${props => (props.$isVisible ? 1 : 0.3)};
`

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  padding: 2px 4px;
  color: #656d76;

  &:hover {
    color: #24292f;
  }
`

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  padding: 2px 4px;
  color: #d1242f;

  &:hover {
    color: #a40e26;
  }
`

const basicTools: Array<{ id: Tool; name: string; icon: string }> = [
  { id: 'select', name: 'Select', icon: '⭳' },
  { id: 'pan', name: 'Pan', icon: '✋' },
  { id: 'measure', name: 'Measure', icon: '📐' },
]

const architecturalTools: Array<{ id: Tool; name: string; icon: string }> = [
  { id: 'wall', name: 'Wall', icon: '🧱' },
  { id: 'door', name: 'Door', icon: '🚪' },
  { id: 'window', name: 'Window', icon: '🪟' },
  { id: 'room', name: 'Room', icon: '🏠' },
  { id: 'polygon', name: 'Polygon', icon: '🔷' },
  { id: 'stairs', name: 'Stairs', icon: '🪜' },
]

const annotationTools: Array<{ id: Tool; name: string; icon: string }> = [
  { id: 'text', name: 'Label', icon: 'T' },
  { id: 'poi', name: 'Point of Interest', icon: '📍' },
]

const fixtureTools: Array<{ id: Tool; name: string; icon: string }> = [
  { id: 'fixture-rect', name: 'Rectangle', icon: '⬜' },
  { id: 'fixture-line', name: 'Line', icon: '➖' },
  { id: 'fixture-point', name: 'Point', icon: '⚙️' },
  { id: 'fixture-circle', name: 'Circle', icon: '⭕' },
  { id: 'fixture-polygon', name: 'Polygon', icon: '⬟' },
]

interface ToolsPanelProps {
  activeTool: Tool
  onToolChange: (tool: Tool) => void
  mapSettings: MapSettings
  onSettingsChange: (updates: Partial<MapSettings>) => void
}

const ToolsPanel: React.FC<ToolsPanelProps> = ({
  activeTool,
  onToolChange,
  mapSettings,
  onSettingsChange,
}) => {
  const { showError, showConfirm } = useModal()
  const [showFloorModal, setShowFloorModal] = useState(false)
  const [editingFloor, setEditingFloor] = useState<FloorLevel | null>(null)
  const [floorForm, setFloorForm] = useState({ name: '', elevation: 0 })
  const [collapsedSections, setCollapsedSections] = useState({
    basic: false,
    architecture: false,
    annotations: false,
    fixtures: false,
    floors: false,
    layers: false,
  })

  const toggleSection = (section: keyof typeof collapsedSections) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handleCreateFloor = () => {
    setEditingFloor(null)
    setFloorForm({ name: '', elevation: 0 })
    setShowFloorModal(true)
  }

  const handleEditFloor = (floor: FloorLevel) => {
    setEditingFloor(floor)
    setFloorForm({ name: floor.name, elevation: floor.elevation })
    setShowFloorModal(true)
  }

  const handleDeleteFloor = async (floorId: number) => {
    if (mapSettings.floors.length <= 1) {
      showError('Cannot delete the last floor. At least one floor is required.')
      return
    }

    await showConfirm(
      'Are you sure you want to delete this floor? All elements on this floor will be removed.',
      () => {
      const updatedFloors = mapSettings.floors.filter(f => f.id !== floorId)
      const newCurrentFloor =
        mapSettings.currentFloor === floorId
          ? updatedFloors[0].id
          : mapSettings.currentFloor

        onSettingsChange({
          floors: updatedFloors,
          currentFloor: newCurrentFloor,
        })
      },
      'Delete Floor'
    )
  }

  const handleToggleFloorVisibility = (floorId: number) => {
    const updatedFloors = mapSettings.floors.map(f =>
      f.id === floorId ? { ...f, isVisible: !f.isVisible } : f,
    )
    onSettingsChange({ floors: updatedFloors })
  }

  const handleSaveFloor = () => {
    if (!floorForm.name.trim()) {
      showError('Floor name is required')
      return
    }

    if (editingFloor) {
      // Update existing floor
      const updatedFloors = mapSettings.floors.map(f =>
        f.id === editingFloor.id
          ? { ...f, name: floorForm.name, elevation: floorForm.elevation }
          : f,
      )
      onSettingsChange({ floors: updatedFloors })
    } else {
      // Create new floor
      const newFloor: FloorLevel = {
        id: Math.max(...mapSettings.floors.map(f => f.id)) + 1,
        name: floorForm.name,
        elevation: floorForm.elevation,
        isVisible: true,
      }
      onSettingsChange({ floors: [...mapSettings.floors, newFloor] })
    }

    setShowFloorModal(false)
  }

  const handleCancelFloor = () => {
    setShowFloorModal(false)
    setEditingFloor(null)
  }

  // Initialize layer visibility if not set
  const layerVisibility = mapSettings.layerVisibility || {
    architecture: 'show',
    fixtures: 'show',
    annotations: 'show',
    measurements: 'show',
  }

  const handleLayerVisibilityChange = (
    layer: keyof typeof layerVisibility,
    visibility: 'show' | 'fade' | 'hide',
  ) => {
    onSettingsChange({
      layerVisibility: {
        ...layerVisibility,
        [layer]: visibility,
      },
    })
  }

  return (
    <PanelContainer>
      <PanelHeader>
        <HeaderTitle>Tools & Layers</HeaderTitle>
      </PanelHeader>

      <LayersSection>
        <SectionHeader
          $isCollapsed={collapsedSections.layers}
          onClick={() => toggleSection('layers')}>
          <SectionTitle>Layer Visibility</SectionTitle>
          <CollapseIcon $isCollapsed={collapsedSections.layers}>▼</CollapseIcon>
        </SectionHeader>
        <SectionContent $isCollapsed={collapsedSections.layers}>
          <div style={{ padding: '8px' }}>
            {Object.entries(layerVisibility).map(([layer, visibility]) => (
              <div key={layer} style={{ marginBottom: '12px' }}>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    marginBottom: '4px',
                    textTransform: 'capitalize',
                  }}>
                  {layer}
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <SmallButton
                    $variant={visibility === 'show' ? 'primary' : undefined}
                    onClick={() =>
                      handleLayerVisibilityChange(
                        layer as keyof typeof layerVisibility,
                        'show',
                      )
                    }
                    style={{ fontSize: '10px', padding: '4px 8px' }}>
                    👁 Show
                  </SmallButton>
                  <SmallButton
                    $variant={visibility === 'fade' ? 'primary' : undefined}
                    onClick={() =>
                      handleLayerVisibilityChange(
                        layer as keyof typeof layerVisibility,
                        'fade',
                      )
                    }
                    style={{ fontSize: '10px', padding: '4px 8px' }}>
                    👁‍🗨 Fade
                  </SmallButton>
                  <SmallButton
                    $variant={visibility === 'hide' ? 'primary' : undefined}
                    onClick={() =>
                      handleLayerVisibilityChange(
                        layer as keyof typeof layerVisibility,
                        'hide',
                      )
                    }
                    style={{ fontSize: '10px', padding: '4px 8px' }}>
                    ❌ Hide
                  </SmallButton>
                </div>
              </div>
            ))}
          </div>
        </SectionContent>
      </LayersSection>

      <ToolSection>
        <SectionHeader
          $isCollapsed={collapsedSections.basic}
          onClick={() => toggleSection('basic')}>
          <SectionTitle>Basic Tools</SectionTitle>
          <CollapseIcon $isCollapsed={collapsedSections.basic}>▼</CollapseIcon>
        </SectionHeader>
        <SectionContent $isCollapsed={collapsedSections.basic}>
          <ToolGrid>
            {basicTools.map(tool => (
              <ToolButton
                key={tool.id}
                $isActive={activeTool === tool.id}
                onClick={() => onToolChange(tool.id)}
                title={`${tool.name} Tool`}>
                <ToolIcon>{tool.icon}</ToolIcon>
                <ToolName>{tool.name}</ToolName>
              </ToolButton>
            ))}
          </ToolGrid>
        </SectionContent>
      </ToolSection>

      <ToolSection>
        <SectionHeader
          $isCollapsed={collapsedSections.architecture}
          onClick={() => toggleSection('architecture')}>
          <SectionTitle>Architecture</SectionTitle>
          <CollapseIcon $isCollapsed={collapsedSections.architecture}>
            ▼
          </CollapseIcon>
        </SectionHeader>
        <SectionContent $isCollapsed={collapsedSections.architecture}>
          <ToolGrid>
            {architecturalTools.map(tool => (
              <ToolButton
                key={tool.id}
                $isActive={activeTool === tool.id}
                onClick={() => onToolChange(tool.id)}
                title={`${tool.name} Tool`}>
                <ToolIcon>{tool.icon}</ToolIcon>
                <ToolName>{tool.name}</ToolName>
              </ToolButton>
            ))}
          </ToolGrid>
        </SectionContent>
      </ToolSection>

      <ToolSection>
        <SectionHeader
          $isCollapsed={collapsedSections.annotations}
          onClick={() => toggleSection('annotations')}>
          <SectionTitle>Annotations</SectionTitle>
          <CollapseIcon $isCollapsed={collapsedSections.annotations}>
            ▼
          </CollapseIcon>
        </SectionHeader>
        <SectionContent $isCollapsed={collapsedSections.annotations}>
          <ToolGrid>
            {annotationTools.map(tool => (
              <ToolButton
                key={tool.id}
                $isActive={activeTool === tool.id}
                onClick={() => onToolChange(tool.id)}
                title={`${tool.name} Tool`}>
                <ToolIcon>{tool.icon}</ToolIcon>
                <ToolName>{tool.name}</ToolName>
              </ToolButton>
            ))}
          </ToolGrid>
        </SectionContent>
      </ToolSection>

      <ToolSection>
        <SectionHeader
          $isCollapsed={collapsedSections.fixtures}
          onClick={() => toggleSection('fixtures')}>
          <SectionTitle>Fixtures</SectionTitle>
          <CollapseIcon $isCollapsed={collapsedSections.fixtures}>
            ▼
          </CollapseIcon>
        </SectionHeader>
        <SectionContent $isCollapsed={collapsedSections.fixtures}>
          <ToolGrid>
            {fixtureTools.map(tool => (
              <ToolButton
                key={tool.id}
                $isActive={activeTool === tool.id}
                onClick={() => onToolChange(tool.id)}
                title={`${tool.name} Tool`}>
                <ToolIcon>{tool.icon}</ToolIcon>
                <ToolName>{tool.name}</ToolName>
              </ToolButton>
            ))}
          </ToolGrid>
        </SectionContent>
      </ToolSection>

      <LayersSection>
        <SectionHeader
          $isCollapsed={collapsedSections.floors}
          onClick={() => toggleSection('floors')}>
          <SectionTitle>Floor Levels</SectionTitle>
          <CollapseIcon $isCollapsed={collapsedSections.floors}>▼</CollapseIcon>
        </SectionHeader>
        <SectionContent $isCollapsed={collapsedSections.floors}>
          <FloorActions>
            <SmallButton $variant="primary" onClick={handleCreateFloor}>
              + Add Floor
            </SmallButton>
          </FloorActions>
          <div>
            {mapSettings.floors.map(floor => (
              <FloorButton
                key={floor.id}
                $isActive={mapSettings.currentFloor === floor.id}
                onClick={() => onSettingsChange({ currentFloor: floor.id })}>
                <FloorInfo>
                  <FloorName>{floor.name}</FloorName>
                  <FloorElevation>
                    {floor.elevation > 0 ? '+' : ''}
                    {floor.elevation}
                    {mapSettings.units === 'feet' ? 'ft' : 'm'}
                  </FloorElevation>
                </FloorInfo>
                <FloorControlButtons>
                  <EyeButton
                    $isVisible={floor.isVisible}
                    onClick={e => {
                      e.stopPropagation()
                      handleToggleFloorVisibility(floor.id)
                    }}
                    title={floor.isVisible ? 'Hide floor' : 'Show floor'}>
                    {floor.isVisible ? '👁' : '👁‍🗨'}
                  </EyeButton>
                  <EditButton
                    onClick={e => {
                      e.stopPropagation()
                      handleEditFloor(floor)
                    }}
                    title="Edit floor">
                    ✏️
                  </EditButton>
                  {mapSettings.floors.length > 1 && (
                    <DeleteButton
                      onClick={e => {
                        e.stopPropagation()
                        handleDeleteFloor(floor.id)
                      }}
                      title="Delete floor">
                      🗑️
                    </DeleteButton>
                  )}
                </FloorControlButtons>
              </FloorButton>
            ))}
          </div>
        </SectionContent>
      </LayersSection>

      {/* Floor Modal */}
      <Modal $isOpen={showFloorModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingFloor ? 'Edit Floor' : 'Add New Floor'}
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Floor Name</Label>
              <Input
                type="text"
                value={floorForm.name}
                onChange={e =>
                  setFloorForm(prev => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., Ground Floor, 2nd Floor"
              />
            </FormGroup>
            <FormGroup>
              <Label>
                Elevation ({mapSettings.units === 'feet' ? 'ft' : 'm'})
              </Label>
              <Input
                type="number"
                step="0.1"
                value={floorForm.elevation}
                onChange={e =>
                  setFloorForm(prev => ({
                    ...prev,
                    elevation: Number(e.target.value),
                  }))
                }
                placeholder="0"
              />
            </FormGroup>
          </ModalBody>
          <ModalActions>
            <Button onClick={handleCancelFloor}>Cancel</Button>
            <Button $variant="primary" onClick={handleSaveFloor}>
              {editingFloor ? 'Update' : 'Create'}
            </Button>
          </ModalActions>
        </ModalContent>
      </Modal>
    </PanelContainer>
  )
}

export { ToolsPanel }
export type { Tool }
